// @flow

import React, { type Node, useRef, useEffect } from 'react';

import { useUniqueId } from '@tectonics/hooks';
import { useUserInputAsTextField, useUserInputValue } from '@tectonics/prs/user-input-values';
import { useUserInputValidationError, type UserInputValidationError, serverErrorMessage } from '@tectonics/prs/validation';
import { useSubmitting } from '@tectonics/prs/submitting';
import { useIncomingPayment, getCountry } from '@tectonics/prs/incoming-payment';
import { useFormHasBeenSent } from '@tectonics/prs/form-status';

import { Input, Label } from '../../../../commons/atoms';
import { Field } from '../../../../commons/molecules';

export function FieldCustomerEmail(): Node {
  const inputId = useUniqueId();
  const submitting = useSubmitting();

  const [, setValue] = useUserInputValue('customerEmail');
  const [value, handleChange] = useUserInputAsTextField('customerEmail');
  const clearEmail = useRef(value.endsWith('@ebanxuber.com'));
  useEffect(() => {
    if (clearEmail.current) {
      setValue('');
    }
  }, [clearEmail.current]);

  const formHasBeenSent = useFormHasBeenSent();
  const validationError = useUserInputValidationError('customerEmail');

  const incomingPayment = useIncomingPayment();
  const country = getCountry(incomingPayment);

  return (
    <Field
      data-test-name="customer-email"
      label={
        <Label htmlFor={inputId}>
          <MailLabelByCountry />
        </Label>
      }
      error={<ValidationErrorMessage validationError={validationError} />}
      hasError={formHasBeenSent && !!validationError}
    >
      <Input
        autoComplete="email"
        id={inputId}
        onChange={handleChange}
        placeholder={getPlaceholderByCountry(country)}
        readOnly={submitting}
        type="email"
        value={value}
        name="customer-email"
      />
    </Field>
  );
}

type ValidationErrorMessageProps = {|
  validationError: ?UserInputValidationError,
|};

function ValidationErrorMessage({ validationError }: ValidationErrorMessageProps): Node {
  const fallback = useInvalidErrorByCountry();

  if(!validationError) {
    return fallback;
  }

  if (validationError.id === 'server-error') {
    return serverErrorMessage(validationError.payload, fallback);
  }

  return fallback;
}

function useInvalidErrorByCountry(): string {
  const incomingPayment = useIncomingPayment();
  const country = getCountry(incomingPayment);

  switch (country) {
    case 'br':
      return 'Email não é valido';

    case 'cl':
    case 'co':
    case 'ec':
    case 'mx':
    case 'pe':
    case 'ar':
    case 'bo':
      return 'Correo Electrónico no es válido';

    default:
      throw new Error(`Field Customer Mail: ${country} has no Invalid Mail Error Message`);
  }
}

function MailLabelByCountry(): string {
  const incomingPayment = useIncomingPayment();
  const country = getCountry(incomingPayment);
  const isMPConnect = incomingPayment.payment_type_code === 'mpconnect';

  if (isMPConnect) {
    return 'Correo Electrónico de Mercado Pago';
  }

  switch (country) {
    case 'br':
      return 'Email';

    case 'cl':
    case 'co':
    case 'ec':
    case 'mx':
    case 'pe':
    case 'ar':
    case 'bo':
    case 'uy':
      return 'Correo Electrónico';

    default:
      throw new Error(`Field Customer Mail: ${country} has no Mail Label mapped`);
  }
}

function getPlaceholderByCountry(country: string): string {
  switch (country) {
    case 'br':
      return 'Insira seu email';

    case 'cl':
    case 'co':
    case 'ec':
    case 'mx':
    case 'pe':
    case 'ar':
    case 'bo':
    case 'uy':
      return 'Ingresa tu correo electrónico';

    default:
      throw new Error(`Field Customer Mail: ${country} has no placeholder mapped`);
  }
}
